
import { Award, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getIndustryBenchmark, Industry } from "@/types/assessment";

interface ExecutiveSummaryProps {
  organizationName: string;
  overallScore: number;
  maturityLevel: string;
  description: string;
  industry: Industry; // Updated to use Industry type
  lowestDimension: string;
  lowestScore: number;
}

export const ExecutiveSummary = ({
  organizationName,
  overallScore,
  maturityLevel,
  description,
  industry,
  lowestDimension,
  lowestScore
}: ExecutiveSummaryProps) => {
  const benchmarkInfo = getIndustryBenchmark(industry, overallScore);

  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 to-secondary/5">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Award className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
          <h2 className="text-2xl sm:text-3xl font-bold text-primary">Executive Summary</h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <div className="text-4xl sm:text-6xl font-bold text-secondary mb-2">{overallScore.toFixed(1)}/5</div>
              <div className="text-xl sm:text-2xl font-semibold text-primary">{maturityLevel}</div>
            </div>
            <Alert className="bg-white">
              <AlertDescription className="text-base">
                {organizationName} demonstrates a {maturityLevel.toLowerCase()} level of AI maturity, 
                scoring {overallScore.toFixed(1)} out of 5. This assessment indicates {description.toLowerCase()} 
                Key areas for improvement include {lowestDimension}, which scored {lowestScore.toFixed(1)}/5, 
                suggesting immediate attention is needed in this dimension.
              </AlertDescription>
            </Alert>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-bold text-primary">Industry Context</h3>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>{benchmarkInfo}</p>
              <p>Within the {industry} sector, organizations at this maturity level typically face challenges in 
              scaling AI initiatives while maintaining governance and risk management frameworks. Your score suggests 
              opportunities for strategic investments in {lowestDimension} to align with industry leaders.</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
